import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App, Button, ColorPicker, Typography } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  organizationsOrganizationsPartialUpdateMutation,
  organizationsOrganizationsRetrieveQueryKey,
  type Organization
} from 'src/api/main'
import { useShowApiErrors } from 'src/utils'

const { Text } = Typography

const DEFAULT_PRIMARY = '#0F766E'

const PRESET_COLORS = [
  '#0F766E',
  '#14B8A6',
  '#0D9488',
  '#2563EB',
  '#0891B2',
  '#1D4ED8',
  '#4F46E5',
  '#7C3AED',
  '#9333EA',
  '#C026D3',
  '#DB2777',
  '#DC2626',
  '#E11D48',
  '#EA580C',
  '#0E9F6E',
  '#16A34A',
  '#22C55E',
  '#D97706',
  '#CA8A04',
  '#EAB308',
  '#F59E0B',
  '#64748B',
  '#334155',
  '#0B1420'
]

type OrganizationWithSettings = Organization & {
  settings?: Record<string, unknown> | null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const getPrimaryColor = (
  settings: OrganizationWithSettings['settings']
): string | null => {
  if (!isRecord(settings) || !isRecord(settings.token)) {
    return null
  }

  const color = settings.token.colorPrimary

  return typeof color === 'string' && color.trim() ? color.trim() : null
}

const buildSettingsPatch = (
  settings: OrganizationWithSettings['settings'],
  colorPrimary: string
): Record<string, unknown> => {
  const base = isRecord(settings) ? settings : {}

  return {
    ...base,
    token: { colorPrimary }
  }
}

type Props = {
  organization: Organization
}

export const PrimaryColorField = ({ organization }: Props) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const showApiErrors = useShowApiErrors()

  const organizationWithSettings = organization as OrganizationWithSettings
  const savedColor =
    getPrimaryColor(organizationWithSettings.settings) ?? DEFAULT_PRIMARY

  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<string | null>(null)

  const { mutateAsync: patchPrimaryColor, isPending: isSaving } = useMutation({
    ...organizationsOrganizationsPartialUpdateMutation(),
    onSuccess: async () => {
      message.success(t('workspace.settings.saved'))
      await queryClient.invalidateQueries({
        queryKey: organizationsOrganizationsRetrieveQueryKey({
          path: { id: organization.id }
        })
      })
    },
    onError: (error) => showApiErrors(error)
  })

  const isDirty =
    draft !== null && draft.toLowerCase() !== savedColor.toLowerCase()

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setDraft(null)
    }
  }

  const handleApply = async () => {
    if (draft === null || !isDirty) {
      return
    }

    await patchPrimaryColor({
      path: { id: organization.id },
      body: {
        settings: buildSettingsPatch(organizationWithSettings.settings, draft)
      } as never
    })
    setDraft(null)
    setOpen(false)
  }

  return (
    <>
      <Text
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--ant-color-text)',
          display: 'block',
          marginBottom: 6
        }}
      >
        {t('workspace.settings.branding.primaryColor')}
      </Text>
      <ColorPicker
        value={draft ?? savedColor}
        open={open}
        onOpenChange={handleOpenChange}
        disabledAlpha
        size="small"
        showText
        presets={[
          {
            label: t('workspace.settings.branding.recommended'),
            colors: PRESET_COLORS
          }
        ]}
        onChange={(color) => setDraft(color.toHexString())}
        panelRender={(panel) => (
          <>
            {panel}
            <Button
              type="primary"
              size="small"
              block
              disabled={!isDirty}
              loading={isSaving}
              onClick={handleApply}
              style={{ marginTop: 8 }}
            >
              {t('workspace.settings.branding.apply')}
            </Button>
          </>
        )}
      />
    </>
  )
}
